import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllVip, getAllGuard } from "../api/vipform";
import { useUserStore } from "../context/UserContext";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import * as ChartJS from "chart.js/auto";

import { getToken } from "firebase/messaging";
import { messaging } from "../api/firebase";

if (window.Chart) {
  delete window.Chart;
}

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function UserDashboard() {
  const navigate = useNavigate();
  const { selectedUser, setSelectedUser, setGuardRanks } = useUserStore();

  const [UserName, setUserName] = useState("User");
  const [vipList, setVipList] = useState([]);
  const [guardList, setGuardList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [totalIncidents, setTotalIncidents] = useState(0);
  const [activeIncidents, setActiveIncidents] = useState(0);
  const [totalNotifications, setTotalNotifications] = useState(0);

  const [showNotifyPopup, setShowNotifyPopup] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* USER PROFILE SYNC */
  useEffect(() => {
    const syncUserProfile = async () => {
      try {
        if (selectedUser?.name) {
          setUserName(selectedUser.name);
          return;
        }

        const stored = localStorage.getItem("selectedUser");
        if (stored) {
          const parsed = JSON.parse(stored);
          setSelectedUser(parsed);
          setUserName(parsed.name);
          return;
        }

        const token = localStorage.getItem("userToken");
        if (!token) return;

        const decoded = jwtDecode(token);
        const username = decoded.sub;

        const res = await axios.get(`${BASE_URL}/usr/profile`, {
          params: { username },
          headers: { Authorization: `Bearer ${token}` },
        });

        const profile = Array.isArray(res.data) ? res.data[0] : res.data;
        setSelectedUser(profile);
        setUserName(profile.name);
        localStorage.setItem("selectedUser", JSON.stringify(profile));
      } catch (err) {
        console.log("User Sync Error:", err);
      }
    };

    syncUserProfile();
  }, [selectedUser, setSelectedUser]);

  /* DATA LOADING */
  const loadVip = async () => {
    const data = await getAllVip();
    setVipList(Array.isArray(data) ? data : data?.data || []);
  };

  const loadGuards = async () => {
    try {
      let all = [];
      let page = 0;
      let totalPages = 1;

      while (page < totalPages) {
        const res = await getAllGuard(page, 100);
        all = [...all, ...(res.content || [])];
        totalPages = res.totalPages;
        page++;
      }

      setGuardList(all);

      const ranks = [
        ...new Set(
          all.map((g) => g.rank || g.designation || g.grade).filter(Boolean)
        ),
      ];
      setGuardRanks(ranks);
    } catch (err) {
      console.log("Guard Load Error:", err);
    }
  };

  const fetchIncidentsCount = async () => {
    try {
      const role = localStorage.getItem("role");
      const token = role === "admin" ? localStorage.getItem("adminToken") : localStorage.getItem("userToken");

      const res = await axios.get(`${BASE_URL}/api/duty/accidentall`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data || [];
      setTotalIncidents(data.length);
      const active = data.filter(
        (i) => !i.req || i.req === "Arise" || i.req?.toLowerCase().includes("pending")
      );
      setActiveIncidents(active.length);
    } catch (err) {
      console.error("Incident count error", err);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const role = localStorage.getItem("role");
      const token = role === "admin" ? localStorage.getItem("adminToken") : localStorage.getItem("userToken");

      const res = await axios.get(`${BASE_URL}/api/notification/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTotalNotifications(Array.isArray(res.data) ? res.data.length : 0);
    } catch (err) {
      console.error("Notification count error", err);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadVip(), loadGuards(), fetchIncidentsCount(), fetchNotificationCount()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  /* LOGOUT */
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* CHART */
  useEffect(() => {
    if (loading) return;

    if (window._pieChart) window._pieChart.destroy();

    const pieCanvas = document.getElementById("pieChart");

    if (pieCanvas) {
      window._pieChart = new ChartJS.Chart(pieCanvas, {
        type: "doughnut",
        data: {
          labels: ["VIPs", "Officers"],
          datasets: [{
            data: [vipList.length, guardList.length],
            backgroundColor: ["#f60ea5ff", "#10b981"],
            borderColor: "#1e293b",
            borderWidth: 3,
            hoverOffset: 12,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "bottom", labels: { padding: 20, font: { size: 14 } } },
          },
          cutout: "70%",
        },
      });
    }
  }, [loading, vipList, guardList]);

  /* NOTIFICATION PERMISSION */
  const handleNotificationPermission = async () => {
    try {
      setNotifyLoading(true);
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setNotifyMessage("Notification permission not accepted.");
        setNotifyLoading(false);
        return;
      }

      const token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });

      if (token) {
        await axios.post(`${BASE_URL}/api/notification/subscribe`, {
          notificationToken: token,
          notificationSenderId: selectedUser?.id,
          notificationSenderRole: "user",
          notificationSenderName: UserName,
          notificationMessage: `${UserName} subscribed Successfully`,
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
        });
      }

      setTimeout(() => {
        setShowNotifyPopup(false);
        setNotifyLoading(false);
      }, 3000);
    } catch (err) {
      console.error(err);
      setNotifyLoading(false);
    }
  };

  useEffect(() => {
    if (Notification.permission === "granted") {
      setShowNotifyPopup(false);
    } else {
      setShowNotifyPopup(true);
      setNotifyMessage("Enable notifications to stay updated.");
    }
  }, []);

  const vipCount = vipList.length;
  const guardCount = guardList.length;
  const totalUsers = vipCount + guardCount;

  const guardGradeStats = guardList.reduce((acc, g) => {
    const grade = g.rank || g.designation || g.grade || "Unknown";
    acc[grade] = (acc[grade] || 0) + 1;
    return acc;
  }, {});

  const isMobile = windowWidth <= 991;

  return (
    <>
      <div className="dashboard-wrapper">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Manager Dashboard</h1>
              <p>Monitor VIPs, Officers & System Overview</p>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              <span className="btn-text">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="dashboard-main">
          {/* Welcome Section */}
          <section className="welcome-card">
            <h2>Welcome back, {UserName} 👋</h2>
            <p className="welcome-subtitle">Here's your system overview at a glance.</p>
          </section>

          {/* Loading State */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading dashboard data...</p>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <section className="stats-grid">
                <StatCard label="Total VIPs" value={vipCount} color="#3b82f6" icon="fa-user-tie" />
                <StatCard
                  label="Total Officers"
                  value={guardCount}
                  color="#10b981"
                  icon="fa-user-shield"
                  breakdown={guardGradeStats}
                />
                <StatCard label="Total Users" value={totalUsers} color="#f59e0b" icon="fa-users" />
                <StatCard label="Active Incidents" value={activeIncidents} color="#ef4444" icon="fa-exclamation-triangle" />
                <StatCard label="Total Incidents" value={totalIncidents} color="#f97316" icon="fa-file-alt" />
                <StatCard label="Notifications" value={totalNotifications} color="#06b6d4" icon="fa-bell" />
              </section>

              {/* Charts & Activity */}
              <section className="charts-section">
                <div className="chart-card">
                  <h3>User Role Distribution</h3>
                  <div className="chart-container">
                    <canvas id="pieChart"></canvas>
                  </div>
                </div>

                <div className="activity-card">
                  <h3>Recent Activity</h3>
                  <ul className="activity-list">
                    <li><i className="fas fa-check"></i> VIP & Officer lists updated</li>
                    <li><i className="fas fa-check"></i> Dashboard loaded successfully</li>
                    <li><i className="fas fa-check"></i> System running smoothly</li>
                    <li><i className="fas fa-bell"></i> Notifications enabled</li>
                  </ul>
                </div>
              </section>
            </>
          )}
        </main>

        {/* Notification Popup */}
        {showNotifyPopup && !isMobile && (
          <div className="notification-toast">
            <div className="toast-header">
              <i className="fas fa-bell"></i>
              <strong>Enable Notifications</strong>
            </div>
            <p>{notifyMessage}</p>
            <button
              onClick={handleNotificationPermission}
              disabled={notifyLoading}
              className="toast-btn"
            >
              {notifyLoading ? "Processing..." : "Allow Notifications"}
            </button>
          </div>
        )}
      </div>

      {/* Unified Modern Styling (same as AdminDashboard) */}
      <style jsx>{`
        .dashboard-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .dashboard-header {
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
          width: 100%;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #60a5fa;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
        }

        .logout-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .logout-btn:hover {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }

        .btn-text { display: inline; }

        .dashboard-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }

        @media (max-width: 768px) {
          .dashboard-main { padding: 1rem; }
          .dashboard-header { padding: 1rem 1.5rem; }
        }

        .welcome-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
        }

        .welcome-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.2);
        }

        .welcome-card h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #ffffff;
        }

        .welcome-subtitle {
          opacity: 0.85;
          margin-bottom: 0;
          font-size: 1rem;
        }

        .loading-container {
          text-align: center;
          padding: 3rem;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.2rem;
          margin-bottom: 1.8rem;
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }
        }

        .charts-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        @media (max-width: 991px) {
          .charts-section { grid-template-columns: 1fr; }
        }

        .chart-card, .activity-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
        }

        .chart-card:hover, .activity-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.15);
        }

        .chart-card h3, .activity-card h3 {
          margin-bottom: 1.2rem;
          font-weight: 600;
          color: #60a5fa;
          font-size: 1.2rem;
        }

        .chart-container {
          height: 280px;
          position: relative;
        }

        .activity-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .activity-list li {
          padding: 0.6rem 0;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.95rem;
        }

        .activity-list li:last-child { border-bottom: none; }
        .activity-list i { color: #10b981; font-size: 0.9rem; }

        .notification-toast {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 320px;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(12px);
          border-radius: 14px;
          padding: 1.2rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          border: 1px solid #3b82f6;
          z-index: 9999;
          animation: slideIn 0.5s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .toast-header {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 0.6rem;
          color: #60a5fa;
        }

        .toast-btn {
          width: 100%;
          padding: 0.6rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          margin-top: 0.8rem;
          transition: all 0.3s;
        }

        .toast-btn:hover { background: #2563eb; }
        .toast-btn:disabled { opacity: 0.7; cursor: not-allowed; }
      `}</style>
    </>
  );
}

/* Reusable StatCard – Identical to AdminDashboard */
function StatCard({ label, value, color, icon, breakdown }) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  return (
    <>
      <div
        className="stat-card"
        onClick={() => breakdown && setShowBreakdown(!showBreakdown)}
        style={{ cursor: breakdown ? "pointer" : "default" }}
      >
        <div className="stat-content">
          <div className="stat-icon" style={{ backgroundColor: color }}>
            <i className={`fas ${icon}`}></i>
          </div>
          <div className="stat-info">
            <p className="stat-label">{label}</p>
            <h3 className="stat-value">{value}</h3>
          </div>
          {breakdown && (
            <i className={`fas fa-chevron-${showBreakdown ? "up" : "down"} chevron`}></i>
          )}
        </div>

        {showBreakdown && breakdown && (
          <div className="breakdown-tooltip">
            {Object.entries(breakdown).map(([grade, count]) => (
              <div key={grade} className="breakdown-item">
                <span>{grade}</span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .stat-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 14px;
          padding: 1.2rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: visible;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.5);
        }

        .stat-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        .stat-info {
          text-align: right;
          flex-grow: 1;
        }

        .stat-label {
          opacity: 0.85;
          font-size: 0.85rem;
          margin-bottom: 0.2rem;
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
          color: #ffffff;
        }

        .chevron {
          opacity: 0.7;
          font-size: 0.9rem;
          color: #94a3b8;
          flex-shrink: 0;
        }

        .breakdown-tooltip {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(15, 23, 42, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 12px;
          padding: 0.8rem;
          margin-top: 0.5rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          border: 1px solid #3b82f6;
          min-width: 160px;
          z-index: 20;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          padding: 0.3rem 0;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
          font-size: 0.85rem;
        }

        .breakdown-item:last-child { border-bottom: none; }

        .breakdown-item span { color: #cbd5e1; }
        .breakdown-item strong { color: #60a5fa; }
      `}</style>
    </>
  );
}

// //------------------------------3---------------------------------------------------------
// import React, { useState, useEffect } from "react";
// import {  useNavigate } from "react-router-dom";
// import { getAllVip, getAllGuard } from "../api/vipform";
// import { useUserStore } from "../context/UserContext";
// import axios from "axios";
// import { jwtDecode } from "jwt-decode";
// import * as ChartJS from "chart.js/auto"; // Option B — module version only

// // ✅ ONLY ADDITION
// import { getToken } from "firebase/messaging";
// import { messaging } from "../api/firebase";


// // Remove global Chart.js to prevent conflicts
// if (window.Chart) {
//   delete window.Chart;
// }

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function UserDashboard() {
//   const [hover, setHover] = useState(false);
//   const navigate = useNavigate();

//   const { selectedUser, setSelectedUser } = useUserStore();

//   const [UserName, setUserName] = useState("User");
//   const [vipList, setVipList] = useState([]);
//   const [guardList, setGuardList] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const [totalIncidents, setTotalIncidents] = useState(0);
//   const [activeIncidents, setActiveIncidents] = useState(0);
//   const [totalNotifications, setTotalNotifications] = useState(0);
  

//     // 🔔 notification popup (ONLY ADDITION)
//     const [showNotifyPopup, setShowNotifyPopup] = useState(false);
//     const [notifyMessage, setNotifyMessage] = useState("");
//     const [notifyLoading, setNotifyLoading] = useState(false);
  
    
  
//   useEffect(() => {
//     if (selectedUser?.name) {
//       setUserName(selectedUser.name);
//     }
//   }, [selectedUser]);

//   useEffect(() => {
//     const syncUserProfile = async () => {
//       try {
//         if (selectedUser?.name) return;

//         const stored = localStorage.getItem("selectedUser");
//         if (stored) {
//           const parsed = JSON.parse(stored);
//           setSelectedUser(parsed);
//           setUserName(parsed.name);
//           return;
//         }

//         const token = localStorage.getItem("userToken");
//         if (!token) return;

//         const decoded = jwtDecode(token);
//         const username = decoded.sub

//         const res = await axios.get(`${BASE_URL}/usr/profile`, {
//           params: { username },
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const profile = Array.isArray(res.data) ? res.data[0] : res.data;

//         setSelectedUser(profile);
//         setUserName(profile.name);
//         localStorage.setItem("selectedUser", JSON.stringify(profile));
//       } catch (err) {
//         console.log("User Sync Error:", err);
//       }
//     };

//     syncUserProfile();
//   }, []);

//   const loadVip = async () => {
//     const data = await getAllVip();
//     setVipList(Array.isArray(data) ? data : data?.data || []);
//   };

//   const loadGuards = async () => {
//     try {
//       let all = [];
//       let page = 0;
//       let totalPages = 1;

//       while (page < totalPages) {
//         const res = await getAllGuard(page, 100);
//         all = [...all, ...(res.content || [])];
//         totalPages = res.totalPages;
//         page++;
//       }

//       setGuardList(all);

//       const ranks = [
//         ...new Set(
//           all.map((g) => g.rank || g.designation || g.grade).filter(Boolean)
//         ),
//       ];

//       setGuardRanks(ranks);
//     } catch (err) {
//       console.log("Guard Load Error:", err);
//     }
//   };

//   useEffect(() => {
//     const loadAll = async () => {
//       setLoading(true);
//       await loadVip();
//       await loadGuards();
//       await fetchIncidentsCount();     // ✅ added
//       await fetchNotificationCount();
//       setLoading(false);
//     };

//     loadAll();
//   }, []);

//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

  
  
//   const vipCount = vipList.length;
//   const guardCount = guardList.length;
//     const guardGradeStats = guardList.reduce((acc, g) => {
//     const grade = g.rank || g.designation || g.grade;
//     if (!grade) return acc;
//     acc[grade] = (acc[grade] || 0) + 1;
//     return acc;
//   }, {});
//   const totalUsers = vipCount + guardCount;


//   /* -----------------------------------
//             CHART.JS — FULL FIXED
//     ------------------------------------ */
//     useEffect(() => {
//       if (loading) return;
  
//       // const vipTrend = [10, 25, 40, 60, 75, vipCount];
//       // const guardTrend = [15, 35, 55, 80, 100, guardCount];
  
//       if (window._lineChart) window._lineChart.destroy();
//       if (window._pieChart) window._pieChart.destroy();
  
//   //    const growthCanvas = document.getElementById("growthChart");
//       const pieCanvas = document.getElementById("pieChart");
  
//       // // LINE CHART
//       // if (growthCanvas) {
//       //   window._lineChart = new ChartJS.Chart(growthCanvas, {
//       //     type: "line",
//       //     data: {
//       //       labels: [
//       //         "Jan", "Feb", "Mar", "Apr", "May", "Jun",
//       //         "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
//       //       ],
//       //       datasets: [
//       //         {
//       //           label: "VIPs",
//       //           data: vipTrend,
//       //           borderColor: "#1e40af",
//       //           backgroundColor: "rgba(30, 64, 175, 0.10)",
//       //           fill: true,
//       //           tension: 0.4,
//       //         },
//       //         {
//       //           label: "Officers",
//       //           data: guardTrend,
//       //           borderColor: "#16a34a",
//       //           backgroundColor: "rgba(22, 163, 74, 0.10)",
//       //           fill: true,
//       //           tension: 0.4,
//       //         },
//       //       ],
//       //     },
//       //     options: { responsive: true },
//       //   });
//       // }
  
//       // PIE CHART
//       if (pieCanvas) {
//         window._pieChart = new ChartJS.Chart(pieCanvas, {
//           type: "doughnut",
//           data: {
//             labels: ["VIPs", "Officers"],
//             datasets: [
//               {
//                 data: [vipCount, guardCount],
//                 backgroundColor: ["#1e40af", "#16a34a"],
//                 borderWidth: 2,
//                 borderColor: "#fff",
//               },
//             ],
//           },
//           options: {
//             responsive: true,
//             plugins: {
//               legend: { position: "bottom" },
//             },
//           },
//         });
//       }
//     }, [loading, vipCount, guardCount]);
  
    
//       /* -----------------------------------
//           🔔 NOTIFICATION (ONLY ADDITION)
//       ------------------------------------ */
//       const handleNotificationPermission = async () => {
//         try {
//           setNotifyLoading(true);
    
//           const permission = await Notification.requestPermission();
//           if (permission !== "granted") {
//             setNotifyMessage("Notification permission not accepted.");
//             setNotifyLoading(false);
//             return;
//           }
    
//           const token = await getToken(messaging, {
//             vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
//           });
    
//           if (token) {
//             await axios.post(
//               `${BASE_URL}/api/notification/subscribe`,
//               {
//                 notificationToken: token,
//                 notificationSenderId: selectedUser?.id,
//                 notificationSenderRole: "user",
//                 notificationSenderName: UserName,
//                 notificationMessage:
//                   `${UserName} subscribed Successfully`,
//               },
//               {
//                 headers: {
//                   Authorization: `Bearer ${localStorage.getItem("userToken")}`,
//                 },
//               }
//             );
//           }
    
//           setTimeout(() => {
//             setShowNotifyPopup(false);
//             setNotifyLoading(false);
//           }, 5000);
//         } catch (err) {
//           console.error(err);
//           setNotifyLoading(false);
//         }
//       };
    
//       useEffect(() => {
//         if (Notification.permission === "granted") {
//           setShowNotifyPopup(false);
//         } else {
//           setShowNotifyPopup(true);
//           setNotifyMessage("Please allow notification permission.");
//         }
//       }, []);

// const fetchIncidentsCount = async () => {
//     try {
//       const role = localStorage.getItem("role");
//       const token =
//         role === "admin"
//           ? localStorage.getItem("adminToken")
//           : localStorage.getItem("userToken");

//       const res = await axios.get(`${BASE_URL}/api/duty/accidentall`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       const data = res.data || [];

//       setTotalIncidents(data.length);

//       // ACTIVE = still pending (Arise / pending)
//       const active = data.filter(
//         (i) =>
//           !i.req ||
//           i.req === "Arise" ||
//           i.req?.toLowerCase().includes("pending")
//       );

//       setActiveIncidents(active.length);
//     } catch (err) {
//       console.error("Incident count error", err);
//     }
//   };

//   const fetchNotificationCount = async () => {
//     try {
//       const role = localStorage.getItem("role");
//       const token =
//         role === "admin"
//           ? localStorage.getItem("adminToken")
//           : localStorage.getItem("userToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/notification/all`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       setTotalNotifications(Array.isArray(res.data) ? res.data.length : 0);
//     } catch (err) {
//       console.error("Notification count error", err);
//     }
//   };


//   return (
//     <div
    
//      style={{
//         backgroundImage:
//           "linear-gradient(135deg,  rgba(145, 195, 243, 0.7),rgba(233, 239, 125, 0.9))",
//       }}
//     >
//       {/* HEADER */}
//       <header  className="shadow-sm border-bottom"
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//         style={{
//           transition: "background 0.3s ease, box-shadow 0.3s ease",
//           boxShadow: hover
//             ? "0 8px 20px rgba(0,0,0,0.15)"
//             : "0 1px 3px rgba(0,0,0,0.1)",
//         }}>
//         <div className="container-fluid px-4 py-3 d-flex justify-content-between align-items-center">
//           <div>
//             <h1 className="h3 mb-0 fw-bold text-primary">Manager Dashboard</h1>
//             <p className="text-muted small mb-0">
//               Manage VIPs, Officers & System Overview
//             </p>
//           </div>

//           <button
//             className="btn btn-danger d-flex align-items-center gap-2"
//             onClick={handleLogout}
//           >
//             <i className="fas fa-sign-out-alt"></i> Logout
//           </button>
//         </div>
//       </header>

//       {/* BODY */}
//       <div className="container-fluid px-4 py-5">
//         {/* WELCOME */}
//         <div className="rounded"
//         onMouseEnter={() => setHover(true)}
//           onMouseLeave={() => setHover(false)}
//           style={{
//             background: "linear-gradient(135deg, rgba(190, 235, 120, 0.45), rgba(120, 200, 255, 0.45))",
//             backdropFilter: "blur(8px)",
//             WebkitBackdropFilter: "blur(8px)",
//             border: hover
//               ? "2px solid #b7d7f4ff"
//               : "2px solid rgba(0,0,0,0.15)",
//             transition: "border 0.3s ease, box-shadow 0.3s ease",
//             boxShadow: hover
//               ? "0 4px 12px rgba(69,159,244,0.3)"
//               : "none",
//             padding: "14px",
//             marginBottom: "20px",
//           }}>
//           <h2 className="fw-bold">
//             Welcome back, {UserName} 👋
//           </h2>
//           <p className="text-muted">Here is today's overview.</p>

//           {/* <div className="mt-4 d-flex gap-3 flex-wrap">
//             <Link to="/vipform" className="btn btn-primary btn-lg px-4">
//               <i className="fas fa-user-plus me-2"></i>Add VIP
//             </Link>

//             <Link to="/guardform" className="btn btn-success btn-lg px-4">
//               <i className="fas fa-shield-alt me-2"></i>Add Officer
//             </Link>
//           </div> */}
//         </div>

//         {/* LOADING SPINNER */}
//         {loading ? (
//           <div className="text-center py-5">
//             <div className="spinner-border text-primary"></div>
//             <p className="text-muted mt-3">Loading dashboard...</p>
//           </div>
//         ) : (
//           <>
//             {/* STATS CARDS */}
//             <div className="row g-4 mb-2">
//               <StatCard
//                 label="Total VIPs"
//                 value={vipCount}
//                 color="bg-primary"
//                 icon="fa-user"
//               />

//               <StatCard
//                 label="Total Officers"
//                 value={guardCount}
//                 color="bg-success"
//                 icon="fa-user-shield"
//               />

//               <StatCard
//                 label="Total Users"
//                 value={totalUsers}
//                 color="bg-warning"
//                 icon="fa-users"
//               />
//               <StatCard label="Active Incidents" value={activeIncidents} color="bg-danger" icon="fa-exclamation-triangle" />
//               <StatCard label="Total Incidents" value={totalIncidents} color="bg-warning" icon="fa-file-alt" />
//               <StatCard label="Total Notifications" value={totalNotifications} color="bg-info" icon="fa-bell" />
//             </div>

           
//             <div className="row justify-content-center g-4 mb-3 d-flex">
//               {/* PIE CHART */}
//               <div className="col-12 col-md-6 col-lg-5 flex-fill">
//                 <div className="card shadow-sm w-90 text-center"
//                  onMouseEnter={() => setHover(true)}
//                   onMouseLeave={() => setHover(false)}
//                   style={{
                    
//                     background: "linear-gradient(135deg, rgba(190, 235, 120, 0.45), rgba(120, 200, 255, 0.45))",
//                     backdropFilter: "blur(8px)",
//                     WebkitBackdropFilter: "blur(8px)",
//                     border: hover
//                       ? "2px solid rgba(69,159,244,0.9)"
//                       : "1px solid rgba(0,0,0,0.15)",
//                     transition: "border-color 0.3s ease",
//                   }}
//                 >
//                   <div className="card-body">
//                     <h5>User Role Distribution</h5>
//                     <div style={{ height: "220px", paddingLeft:"900px"}}>
//                       <canvas id="pieChart"></canvas>
//                       </div>
//                   </div>
//                 </div>
//               </div>
             
//               <div className="col-12 col-md-6 col-lg-5 flex-fill">
//                 <div className="card shadow-sm w-90"
//                 onMouseEnter={() => setHover(true)}
//                   onMouseLeave={() => setHover(false)}
//                   style={{
                    
//                     background: "linear-gradient(135deg, rgba(190, 235, 120, 0.45), rgba(120, 200, 255, 0.45))",
//                     backdropFilter: "blur(8px)",
//                     WebkitBackdropFilter: "blur(8px)",
//                     border: hover
//                       ? "2px solid rgba(69,159,244,0.9)"
//                       : "1px solid rgba(0,0,0,0.15)",
//                     transition: "border-color 0.3s ease",
//                   }}>
//                   <div className="card-body">
//                                      <div style={styles.activityBox}>
//                         <h3 style={styles.activityTitle}>Recent Activity</h3>
//                         <ul style={styles.activityList}>
//                           <li>✔ VIP & Guards list updated</li>
//                           <li>✔ Dashboard loaded successfully</li>
//                           <li>✔ System running smoothly</li>
//                         </ul>
                      
//                     </div>

//                   </div>
//                 </div>
//               </div>

//             </div>
//           </>
//         )}
//       </div>
      
//       {showNotifyPopup && (
//         <div style={{
//           position: "fixed",
//           bottom: 20,
//           right: 20,
//           width: 280,
//           background: "#fff",
//           padding: 14,
//           borderRadius: 8,
//           boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
//         }}>
//           <h6>🔔 Notification Required</h6>
//           <p>{notifyMessage}</p>
//           <button
//             className="btn btn-primary w-100"
//             disabled={notifyLoading}
//             onClick={handleNotificationPermission}
//           >
//             {notifyLoading ? "Please wait..." : "Allow Notifications"}
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// /* -----------------------------------
//         STAT CARD (UNCHANGED)
// ------------------------------------ */
// function StatCard({ label, value, color, icon, breakdown }) {
//   const [show, setShow] = useState(false);

//   return (
//     <div className="col-12 col-sm-6 col-md-4 col-lg-2" style={{ overflow: "visible" }}>
//       <div
//         className="card shadow-sm position-relative h-100"
//         onClick={() => breakdown && setShow(!show)}
//         style={{
//           cursor: breakdown ? "pointer" : "default",
//           background:
//             "linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45))",
//           backdropFilter: "blur(8px)",
//           border: "1px solid rgba(0,0,0,0.15)",
//         }}
//       >
//         <div className="card-body d-flex align-items-center justify-content-between">
//           <div className="d-flex align-items-center">
//             <div className={`${color} me-3 p-2 rounded`}>
//               <i className={`fas ${icon}`} style={{ color: "#fff" }} />
//             </div>
//             <div>
//               <h6 className="text-muted mb-1">{label}</h6>
//               <h4 className="fw-bold mb-0">{value}</h4>
//             </div>
//           </div>

//           {breakdown && (
//             <i
//               className={`fas fa-chevron-${show ? "up" : "down"}`}
//               style={{ opacity: 0.6 }}
//             />
//           )}
//         </div>

//         {show && breakdown && (
//           <div
//             style={{
//               position: "absolute",
//               top: "10%",
//               right: 10,
//               background:
//                 "linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45))",
//               borderRadius: 6,
//               padding: "8px 12px",
//               boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
//               fontSize: 13,
//               zIndex: 10,
//               minWidth: 110,
//             }}
//           >
//             {Object.entries(breakdown).map(([grade, count]) => (
//               <div
//                 key={grade}
//                 className="d-flex justify-content-between"
//               >
//                 <span>{grade}</span>
//                 <strong>{count}</strong>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ================= YOUR STYLES UNCHANGED ================= */
// const styles = {
//   activityBox: {
//     marginTop: "2%",
//     padding: 20,
//     borderRadius: 12,
//     background: "linear-gradient(135deg, rgba(190, 235, 120, 0.45), rgba(120, 200, 255, 0.45))",
//     border: "1px solid #e5e9f0",
//   },
//   activityTitle: {
//     fontSize: 20,
//     color: "#1e73be",
//     marginBottom: 10,
//   },
//   activityList: { lineHeight: 2, paddingLeft: 10,},
// };
